import { TFunction } from "i18next";
import {
	OrderDetailsAPIExport,
	OrderExportInfo,
	OrderSampleTestInfo,
} from "node-library/dist/apiDataFormats";
import {
	DataHandleOption,
	LicenseDesignation,
	LicenseType,
	ReceivingCondition,
	TestHierarchyNodeType,
	WeightUnit,
} from "node-library/dist/constants";
import { deepClone } from "node-library/dist/helpers/misc_helper";
import { createUUID } from "node-library/dist/helpers/request_helper";
import {
	FrontendSample,
	FrontendSampleInstance,
	FrontendSampleSpecification,
	FrontendClient,
	FrontendOrder,
	FrontendOrganization,
	FrontendOrganizationLicense,
	FrontendCompanyTestCategory,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestPackage,
	FrontendCompanyTestHierarchyNode,
} from "node-library/dist/portal";

export function getDescriptionTextForHandleOption(
	option: DataHandleOption,
	translation: TFunction<"translation", undefined>,
): string {
	switch (option) {
		case DataHandleOption.USE_IN_RECORD:
			return translation("UseInfoInRecord");
		case DataHandleOption.USE_NEW:
			return translation("UpdateInfo");
	}
}

export function findAssociatedClient(
	orgClients: Array<FrontendClient>,
	firstName: string,
	lastName: string,
	email: string,
	phoneNumber: string,
): FrontendClient | undefined {
	let associatedClient: FrontendClient | undefined = undefined;
	let score: number = -1;
	for (const orgClient of orgClients) {
		if (orgClient.firstName != firstName || orgClient.lastName != lastName) {
			continue;
		}

		//match
		let currScore: number = 0;
		if (orgClient.email == email) {
			currScore += 2;
		}
		if (orgClient.phoneNumber == phoneNumber) {
			currScore += 1;
		}
		if (currScore > score) {
			score = currScore;
			associatedClient = orgClient;
		}
	}
	return associatedClient;
}

export function findAssociatedLicense(
	orgLicenses: Array<FrontendOrganizationLicense>,
	licenseNumber: string,
	licenseDesignation: LicenseDesignation,
	licenseType: LicenseType,
): FrontendOrganizationLicense | undefined {
	if (!licenseNumber) {
		return undefined;
	}

	return orgLicenses.find((license) => {
		return (
			license.licenseNumber == licenseNumber &&
			license.licenseType == licenseType &&
			license.licenseDesignation == license.licenseDesignation
		);
	});
}

export function constructFrontendOrderFromExportData(
	barcodeId: string,
	orgInfo: FrontendOrganization,
	associatedOrgClient: FrontendClient,
	selectedLicense: FrontendOrganizationLicense | undefined,
	orgSamples: Array<FrontendSample>,
	organizationSpecifications: Array<FrontendSampleSpecification>,
	orderInfo: OrderDetailsAPIExport,
	cannabisTestCategory: FrontendCompanyTestCategory,
): { newSamples: Array<FrontendSample>; order: FrontendOrder } {
	const orderDetails: OrderExportInfo = orderInfo.orderInfo;
	const sampleTestInfos: Array<OrderSampleTestInfo> =
		orderInfo.orderSampleTestsInfo;

	const { newSamples, sampleInstances } =
		convertExportedTestsToFrontendSampleInstance(
			sampleTestInfos,
			organizationSpecifications,
			orgSamples,
			cannabisTestCategory,
		);

	const order: FrontendOrder = {
		orderId: createUUID(),
		orderBarcodeId: barcodeId,
		orderCategoryId: cannabisTestCategory.testCategoryId,

		organizationId: orgInfo.organizationId,
		orderedBy: `${associatedOrgClient.firstName} ${associatedOrgClient.lastName}`,
		email: associatedOrgClient.email,
		phoneNumber: associatedOrgClient.phoneNumber,
		clientCompanyName: orgInfo.clientCompanyName,
		address: orgInfo.address,
		city: orgInfo.city,
		province: orgInfo.province,
		country: orgInfo.country,
		postalCode: orgInfo.postalCode,
		sampleCount: orderDetails.sampleCount,
		licenseId: selectedLicense?.licenseId,
		licenseNumber: selectedLicense?.licenseNumber,
		licenseType: selectedLicense?.licenseType,
		licenseDesignation: selectedLicense?.licenseDesignation,
		billingName:
			orderDetails.billingName ||
			`${associatedOrgClient.firstName} ${associatedOrgClient.lastName}`,
		billingAddress: orderDetails.billingAddress || orgInfo.address,
		billingCity: orderDetails.billingCity || orgInfo.city,
		billingProvince: orderDetails.billingProvince || orgInfo.province,
		billingCountry: orderDetails.billingCountry || orgInfo.country,
		billingPostalCode: orderDetails.billingPostalCode || orgInfo.postalCode,
		billingPhoneNumber:
			orderDetails.billingPhoneNumber || associatedOrgClient.phoneNumber,
		billingEmail: orderDetails.billingEmail || associatedOrgClient.email,
		paid: orderDetails.paid,
		sampleInstances: sampleInstances,
		orderStatus: orderDetails.orderStatus,
		inserted: new Date(orderDetails.inserted),
		paymentTime: orderDetails.paymentTime
			? new Date(orderDetails.paymentTime)
			: undefined,
		completeTime: orderDetails.completeTime
			? new Date(orderDetails.completeTime)
			: undefined,
		testReportCopyEmails: orderDetails.testReportCopyEmails,
		billingInfoCopyEmails: orderDetails.billingInfoCopyEmails,

		comments: orderDetails.comments,
	};
	return {
		newSamples,
		order,
	};
}

function convertExportedTestsToFrontendSampleInstance(
	sampleTestInfos: Array<OrderSampleTestInfo>,
	organizationSpecifications: Array<FrontendSampleSpecification>,
	orgSamples: Array<FrontendSample>,
	testCategory: FrontendCompanyTestCategory,
): {
	newSamples: Array<FrontendSample>;
	sampleInstances: Array<FrontendSampleInstance>;
} {
	const orgSamplesCopy: Array<FrontendSample> = deepClone(orgSamples);
	const newSamples: Array<FrontendSample> = [];

	const currentTime: Date = new Date();
	const results: Array<FrontendSampleInstance> = [];
	for (const testInfo of sampleTestInfos) {
		let matchingSample: FrontendSample;
		let matchingSamples: Array<FrontendSample> = orgSamplesCopy.filter(
			(sample) => {
				return sample.lotCode == testInfo.lotCode;
			},
		);
		const multipleSamplesDetectedWarning: boolean = matchingSamples.length > 1;

		if (matchingSamples.length == 0) {
			//create new sample with the info
			const matchingMatrix: FrontendCompanySampleMatrix | undefined =
				testCategory.sampleMatrices.find((matrix) => {
					return (
						matrix.matrix == testInfo.matrix &&
						matrix.subMatrix == testInfo.subMatrix
					);
				});
			matchingSample = {
				sampleId: createUUID(),
				lotCode: testInfo.lotCode,
				testCategoryId: testCategory.testCategoryId,
				sampleName: testInfo.sampleName,
				sampleDescription: testInfo.sampleDescription,
				matrix: testInfo.matrix,
				matrixId: matchingMatrix?.matrixId || "",
				sampleAdditionalInfos: [],

				isCustomMatrix: matchingMatrix ? false : true,
				isNewSample: true,
			};
			orgSamplesCopy.push(matchingSample);
			newSamples.push(matchingSample);
		} else {
			matchingSample = matchingSamples[0];
		}

		let matchingSpecification: FrontendSampleSpecification | undefined;

		if (testInfo.specificationId) {
			matchingSpecification = organizationSpecifications.find((spec) => {
				return spec.specificationId === testInfo.specificationId;
			});
		}

		const testGroupWithTests: Set<string> = new Set();

		const enabledTestNodeIds: Array<string> = [];

		// --- NEW MYCOTOXIN LOGIC ---
		let updatedOtherInstructions = testInfo.otherInstructions || "";
		const isAfla = testInfo.mycotoxinsAlfatoxins;
		const isOchra = testInfo.mycotoxinsOchratoxin;

		if (isAfla || isOchra) {
			// Find the ID for the single "mycotoxins" node
			const mycoNode = testCategory.hierarchyNodes.find(
				(n) => n.nodeKey === "mycotoxins",
			);
			if (mycoNode) {
				enabledTestNodeIds.push(mycoNode.nodeId);
				if (mycoNode.parentNodeId)
					testGroupWithTests.add(mycoNode.parentNodeId);
			}

			// Append exclude text if one is missing
			if (isAfla && !isOchra) {
				const excludeText = "Exclude Mycotoxins Ochratoxin A";
				updatedOtherInstructions = updatedOtherInstructions
					? `${updatedOtherInstructions}, ${excludeText}`
					: excludeText;
			} else if (!isAfla && isOchra) {
				const excludeText = "Exclude Mycotoxins Aflatoxins";
				updatedOtherInstructions = updatedOtherInstructions
					? `${updatedOtherInstructions}, ${excludeText}`
					: excludeText;
			}
		}
		// -----------------------------

		for (const hierarchyNode of testCategory.hierarchyNodes) {
			const testKey: string = hierarchyNode.nodeKey;

			// Skip the standard mapping for mycotoxins since we handled it above
			if (testKey === "mycotoxins") continue;

			switch (hierarchyNode.nodeType) {
				//expecting these types

				case TestHierarchyNodeType.TEST:
					if ((testInfo as any)[testKey]) {
						enabledTestNodeIds.push(hierarchyNode.nodeId);
						testGroupWithTests.add(hierarchyNode.parentNodeId as string);
					}
					break;
				case TestHierarchyNodeType.TEST_OPTION_RADIO_GROUP:
					if ((testInfo as any)[testKey]) {
						const childNode = testCategory.hierarchyNodes.find((node) => {
							return (
								node.parentNodeId == hierarchyNode.nodeId &&
								node.nodeKey == (testInfo as any)[testKey]
							);
						});
						if (childNode) {
							enabledTestNodeIds.push(childNode.nodeId);
						}
					}
					break;
			}
		}
		//check for test group options (radio or checkbox, add if any test enabled)
		for (const testNodeId of Array.from(testGroupWithTests)) {
			const radioOptionChildNodes: Array<FrontendCompanyTestHierarchyNode> =
				testCategory.hierarchyNodes.filter((node) => {
					return (
						node.nodeType ===
							TestHierarchyNodeType.TEST_GROUP_OPTION_RADIO_GROUP &&
						node.parentNodeId === testNodeId
					);
				});
			for (const child of radioOptionChildNodes) {
				const testKey = child.nodeKey;
				if ((testInfo as any)[testKey]) {
					const childNode = testCategory.hierarchyNodes.find((node) => {
						return (
							node.parentNodeId == child.nodeId &&
							node.nodeKey == (testInfo as any)[testKey]
						);
					});
					if (childNode) {
						enabledTestNodeIds.push(childNode.nodeId);
					}
				}
			}
		}

		const matchingPackage: FrontendCompanyTestPackage | undefined =
			testCategory.testPackages.find((pack) => {
				return pack.packageKey == testInfo.packageTemplate;
			});

		const converted: FrontendSampleInstance = {
			sampleInstanceId: testInfo.sampleTestId,
			sample: matchingSample,
			packageId: matchingPackage?.packageId || "",
			weight: String(testInfo.weight),
			weightUnit: testInfo.weightUnit as WeightUnit,
			isRetest: testInfo.isRetest,
			isAdditionalTest: false,
			treatment: testInfo.treatment,
			otherInstructions: updatedOtherInstructions,
			sampleTestInfo: {
				selectedTests: enabledTestNodeIds.map((id) => {
					return { selectedNodeId: id, assignedBatchId: undefined };
				}),
				expectationInputs: {},
				additionalTest: testInfo.additionalTestDescription ? true : false,
				additionalTestDescription: testInfo.additionalTestDescription,
				turnaroundTime: testInfo.turnaroundTime,
				status: testInfo.status,
			},
			receivedTime: currentTime,
			receivingCondition: ReceivingCondition.AMBIENT,
			expectedCompletionTime: undefined,
			pendingReview: true,
			multipleMatchingSamplesWarning: multipleSamplesDetectedWarning,

			specification: matchingSpecification,
			sampleInstanceAdditionalInfos: [],
		};
		results.push(converted);
	}
	return { newSamples, sampleInstances: results };
}
